import React, { useEffect, useState } from "react";

/**
 * Live/Delayed/Stale data badge.
 *
 * Props:
 *   status        "live" | "delayed" | "stale" | "loading"
 *   lastEventAt   ms epoch — when we last received a snapshot
 *   liveCount     number — symbols currently streaming live
 *   total         number — universe size
 *   className     extra classes
 */
export default function DataBadge({
  status = "loading",
  lastEventAt = 0,
  liveCount = 0,
  total = 0,
  className = "",
}) {
  // Re-tick once a second so the "updated Ns ago" stays current
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const ageSec = lastEventAt ? Math.max(0, Math.round((Date.now() - lastEventAt) / 1000)) : null;

  const meta = {
    live:    { label: "LIVE",    color: "var(--bull)", glow: "rgba(0,255,156,0.45)" },
    delayed: { label: "DELAYED", color: "#ffd166",     glow: "rgba(255,209,102,0.35)" },
    stale:   { label: "STALE",   color: "var(--bear)", glow: "rgba(255,71,87,0.40)" },
    loading: { label: "CONNECTING", color: "#9aa0aa",  glow: "rgba(154,160,170,0.25)" },
  };
  const m = meta[status] || meta.loading;

  const title =
    status === "live"
      ? `Live Finnhub stream · ${liveCount}/${total || "?"} tickers active${ageSec != null ? ` · updated ${ageSec}s ago` : ""}`
      : status === "delayed"
        ? `Showing last-trade prices (markets closed or out of session). Updated ${ageSec != null ? `${ageSec}s ago` : "recently"}.`
        : status === "stale"
          ? `Live stream is currently unreachable — reconnecting. ${ageSec != null ? `Last update ${ageSec}s ago.` : ""}`
          : "Connecting to live market data…";

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 border backdrop-blur-md ${className}`}
      style={{
        borderColor: `${m.color}40`,
        boxShadow: status === "live" ? `0 0 14px ${m.glow}` : "none",
      }}
      title={title}
      data-testid={`data-badge-${status}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full pulse-dot"
        style={{ background: m.color, boxShadow: `0 0 8px ${m.glow}` }}
      />
      <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: m.color }}>
        {m.label}
      </span>
      {status === "live" && total > 0 && (
        <span className="text-[10px] font-mono text-white/55 tabular-nums">
          {liveCount}/{total}
        </span>
      )}
      {ageSec != null && status !== "loading" && (
        <span
          className="text-[10px] font-mono text-white/45 tabular-nums"
          data-testid="data-badge-age"
        >
          · {ageSec}s
        </span>
      )}
    </span>
  );
}
